import * as THREE from 'three'
import { Text } from 'troika-three-text'

import BaseObject from './baseObject'
import MathsUtils from '@/utils/mathsUtils'

import GlassDesignerTypes from '@glass-designer/types'

// Base Dimension Object
// Class for all dimensions to inherit from
export default class BaseDimension extends BaseObject {
  constructor(editor, options) {
    super(editor)

    // Link info to the shape / segment / hole / etc
    this.glassSegmentIndex = options.glassSegmentIndex || 0
    this.glassShape = options.glassShape || null
    this.glassHole = options.glassHole || null
    this.linkData = options.linkData || null

    // Actual measurement points
    this.points = []

    // Settings
    this.arrowLength = 8
    this.arrowWidth = 6
    this.arrowScalarMax = 3.0
    this.textScalar = 0.9
    this.textScalarMax = 3.25  // Maximum size to scale the text. Stops text getting too big when zoomed right out
    this.fontSize = 15
    this.highlightFontSize = 16

    this.labelOffset = 3      // How far to offset label from line

    this.isReadOnly = false
    this.isErrorFlag = false    // Used to display errors when adjusting dimensions
    this._isUserFixed = false   // Calculated from GlassSegment during building
    this.isHighlighted = false  // Used when editing objects that are associated with dimensions

    this.arrowList = []
    this.labelList = []
    this.lineList = []
    this.geometryList = []
    this.materialList = []

    // Released setting. Used internally by dimension helper
    this.isReleasedDimension = false

    this.sceneObject = new THREE.Object3D()
    this.editor.scene.add(this.sceneObject)
  }

  // Reset the settings & state
  reset() {
    this.points = []

    // Settings
    this.arrowLength = 8
    this.arrowWidth = 7
    this.arrowScalarMax = 3.0
    this.textScalar = 0.9
    this.textScalarMax = 3.25  // Maximum size to scale the text. Stops text getting too big when zoomed right out
    this.fontSize = 15
    this.highlightFontSize = 16

    this.labelOffset = 3      // How far to offset label from line

    this.isErrorFlag = false
    this.isReadOnly = false
    this._isUserFixed = false
    this.isHighlighted = false
  }

  // onBeforeRender
  // Called prior to rendering of the object
  onBeforeRender() {
    super.onBeforeRender()

    var dimScale = 1.0 / this.editor.camera.zoom

    var arrowScale = Math.min(dimScale, this.arrowScalarMax)
    this.arrowList.forEach(arrow => {
      arrow.scale.x = arrowScale
      arrow.scale.y = arrowScale
      arrow.scale.z = arrowScale
    })
    
    var labelScale = Math.min(dimScale * this.textScalar, this.textScalarMax)
    this.labelList.forEach(label => {
      label.scale.x = labelScale
      label.scale.y = -labelScale   // Note: Flipped on Y axis as the text lib seems to be a different Y coord system to ours
      label.scale.z = labelScale
    })
  }

  // Dispose all allocated resources & remove objects from the scene
  dispose() {
    this.arrowList.forEach(obj => {
      this.editor.scene.remove(obj)
      this.sceneObject.remove(obj)
    })
    this.labelList.forEach(obj => {
      this.editor.scene.remove(obj)
      this.sceneObject.remove(obj)
      if(obj.material)
        obj.material.dispose()
      obj.dispose()
    })
    this.lineList.forEach(obj => {
      this.editor.scene.remove(obj)
      this.sceneObject.remove(obj)
    })
    this.geometryList.forEach(geometry => {
      geometry.dispose()
    })
    this.materialList.forEach(material => {
      material.dispose()
    })

    this.arrowList = []
    this.labelList = []
    this.lineList = []
    this.points = []
    this.geometryList = []
    this.materialList = []

    super.dispose()
  }

  // Set the error flag for display
  setErrorFlag(errorFlag) {
    this.isErrorFlag = errorFlag
  }

  // Set the highlighted flag
  setHighlighted(highlighted) {
    this.isHighlighted = highlighted
  }

  // Clear the data points
  clearPoints() {
    this.points = []
  }

  // Create an arrow head mesh
  createArrow() {
    var dimensionGeometry = new THREE.BufferGeometry()
    var vertices = []
    var indices = []

    var opacityScalar = 1.0

    // Dim for shape editing mode
    if(this.editor.isShapeEditingMode())
      opacityScalar = 0.33

    vertices.push(0, 0, 0)
    vertices.push(this.arrowLength, -this.arrowWidth/2, 0)
    vertices.push(this.arrowLength, this.arrowWidth/2, 0)
    indices.push(2, 1, 0)

    dimensionGeometry.setAttribute( 'position', new THREE.Float32BufferAttribute( vertices, 3 ) )
    dimensionGeometry.setIndex(indices)

    var colour = this.editor.getTheme().dimArrowColour
    var opacity = this.editor.getTheme().dimArrowOpacity * opacityScalar
    if(this.isErrorFlag) {
      colour = this.editor.getTheme().dimErrorColour
      opacity = this.editor.getTheme().dimErrorOpacity
    }

    var arrowMaterial = new THREE.MeshBasicMaterial({
      color: colour,
      transparent: true,
      opacity: opacity
    })
    let arrowObject = new THREE.Mesh(dimensionGeometry, arrowMaterial)

    this.arrowList.push(arrowObject)
    this.sceneObject.add(arrowObject)
    this.geometryList.push(dimensionGeometry)
    this.materialList.push(arrowMaterial)

    return arrowObject
  }

  // Create a dimension line
  createDimensionLine(startVec, endVec, colour, opacity) {

    var dimensionGeometry = new THREE.BufferGeometry()
    var vertices = []

    // First point is origin, and the object position is set to the first point

    vertices.push(0, 0, 0)
    vertices.push(endVec.x - startVec.x, endVec.y - startVec.y, endVec.z - startVec.z)

    dimensionGeometry.setAttribute( 'position', new THREE.Float32BufferAttribute( vertices, 3 ) );

    if(this.isErrorFlag) {
      colour = this.editor.getTheme().dimErrorColour
      opacity = this.editor.getTheme().dimErrorOpacity
    }

    var lineMaterial = new THREE.LineBasicMaterial({
      color: colour,
      transparent: true,
      opacity: opacity
    })
    let lineObj = new THREE.LineSegments(dimensionGeometry, lineMaterial)
    lineObj.position.copy(startVec)

    this.lineList.push(lineObj)
    this.sceneObject.add(lineObj)
    this.geometryList.push(dimensionGeometry)
    this.materialList.push(lineMaterial)

    return lineObj
  }

  // Update an existing dimension line
  updateDimensionLine(lineObj, startVec, endVec, colour) {
    var vertices = []
    vertices.push(0, 0, 0)
    vertices.push(endVec.x - startVec.x, endVec.y - startVec.y, endVec.z - startVec.z)

    var opacity = this.editor.getTheme().dimLineOpacity
    if(this.isErrorFlag) {
      colour = this.editor.getTheme().dimErrorColour
      opacity = this.editor.getTheme().dimErrorOpacity
    }

    lineObj.geometry.setAttribute('position', new THREE.Float32BufferAttribute( vertices, 3 ) );
    lineObj.material.color.set(colour)
    lineObj.material.opacity = opacity
    lineObj.position.copy(startVec)

    lineObj.geometry.computeBoundingBox()
    lineObj.geometry.computeBoundingSphere()
  }

  // Create a squared angle marker
  createDimensionSquaredAngle(displayPoint, startPoint, midPoint, endPoint, colour) {
    var dimensionGeometry = new THREE.BufferGeometry()
    var vertices = []

    vertices.push(startPoint.x, startPoint.y, startPoint.z)
    vertices.push(midPoint.x, midPoint.y, midPoint.z)
    vertices.push(midPoint.x, midPoint.y, midPoint.z)
    vertices.push(endPoint.x, endPoint.y, endPoint.z)

    dimensionGeometry.setAttribute( 'position', new THREE.Float32BufferAttribute( vertices, 3 ) );

    var opacity = this.editor.getTheme().dimSquareOpacity
    if(this.isErrorFlag) {
      colour = this.editor.getTheme().dimErrorColour
      opacity = this.editor.getTheme().dimErrorOpacity
    }

    var lineMaterial = new THREE.LineBasicMaterial({
      color: colour,
      transparent: true,
      opacity: opacity
    })
    let lineObj = new THREE.LineSegments(dimensionGeometry, lineMaterial)
    lineObj.position.set(displayPoint.x, displayPoint.y, 0)

    this.lineList.push(lineObj)
    this.sceneObject.add(lineObj)
    this.geometryList.push(dimensionGeometry)
    this.materialList.push(lineMaterial)

    return lineObj
  }

  // Update an existing dimension squared angle object
  updateDimensionSquaredAngle(squareObj, displayPoint, startPoint, midPoint, endPoint, colour) {
    var vertices = []

    vertices.push(startPoint.x, startPoint.y, startPoint.z)
    vertices.push(midPoint.x, midPoint.y, midPoint.z)
    vertices.push(midPoint.x, midPoint.y, midPoint.z)
    vertices.push(endPoint.x, endPoint.y, endPoint.z)

    var opacity = this.editor.getTheme().dimSquareOpacity
    if(this.isErrorFlag) {
      colour = this.editor.getTheme().dimErrorColour
      opacity = this.editor.getTheme().dimErrorOpacity
    }

    squareObj.geometry.setAttribute( 'position', new THREE.Float32BufferAttribute( vertices, 3 ) );
    squareObj.material.color.set(colour)
    squareObj.material.opacity = opacity
    squareObj.position.set(displayPoint.x, displayPoint.y, 0)
    squareObj.scale.set(1, 1, 1)

    squareObj.geometry.computeBoundingBox()
    squareObj.geometry.computeBoundingSphere()
  }

  // Create a dimension arc
  createDimensionArc(centerPoint, radius, startAngle, endAngle, colour, opacity) {
    var dimensionGeometry = new THREE.BufferGeometry()
    var vertices = this._generateArcVertices(radius, startAngle, endAngle)

    if(vertices.length == 0)
      return null

    dimensionGeometry.setAttribute( 'position', new THREE.Float32BufferAttribute( vertices, 3 ) );

    if(this.isErrorFlag) {
      colour = this.editor.getTheme().dimErrorColour
      opacity = this.editor.getTheme().dimErrorOpacity
    }

    var lineMaterial = new THREE.LineBasicMaterial({
      color: colour,
      transparent: true,
      opacity: opacity
    })
    let lineObj = new THREE.LineSegments(dimensionGeometry, lineMaterial)
    lineObj.position.copy(centerPoint)

    this.lineList.push(lineObj)
    this.sceneObject.add(lineObj)
    this.geometryList.push(dimensionGeometry)
    this.materialList.push(lineMaterial)

    return lineObj
  }

  // Update an existing dimension arc object
  updateDimensionArc(arcObj, centerPoint, radius, startAngle, endAngle, colour, opacity) {
    var vertices = this._generateArcVertices(radius, startAngle, endAngle)

    if(vertices.length == 0)
      return

    if(this.isErrorFlag) {
      colour = this.editor.getTheme().dimErrorColour
      opacity = this.editor.getTheme().dimErrorOpacity
    }

    arcObj.geometry.setAttribute( 'position', new THREE.Float32BufferAttribute( vertices, 3 ) );
    arcObj.material.color.set(colour)
    arcObj.material.opacity = opacity
    arcObj.position.copy(centerPoint)

    arcObj.geometry.computeBoundingBox()
    arcObj.geometry.computeBoundingSphere()
  }

  // Create a dimension label
  createDimensionLabel(position, text) {
    let labelColour = this.editor.getTheme().dimLabelColour
    let fixedLabelColour = this.editor.getTheme().dimFixedLabelColour

    let textObj = new Text()
    textObj.text = text
    textObj.position.set(position.x, position.y, position.z)
    textObj.color = this._isUserFixed ? fixedLabelColour : labelColour
    textObj.fontSize = this.fontSize
    textObj.anchorX = 'center'
    textObj.anchorY = 'bottom'

    if(this.isReadOnly) {
      textObj.color = labelColour
      textObj.material.opacity = this.editor.getTheme().dimReadOnlyOpacity
    }

    if(this.isErrorFlag) {
      textObj.color = this.editor.getTheme().dimErrorColour
      textObj.material.opacity = this.editor.getTheme().dimErrorOpacity
    }

    this.labelList.push(textObj)
    this.sceneObject.add(textObj)

    textObj.addEventListener('synccomplete', () => {
      this.editor.flagRenderRequired()
    })

    return textObj
  }


  /**
   * Generate the vertices that make up the dimension arc
   * Returns an array of floats to be passed directly into the BufferGeometry object
   * Returns an empty array if the arc has invalid settings
   */
  _generateArcVertices(radius, startAngle, endAngle) {
    let arcSegmentDivisions = 20
    var vertices = []

    // Ignore if the angle distance is too small
    if(Math.abs(endAngle - startAngle) < 1)
      return []

    // Create points to arc
    let angleStep = Math.abs(endAngle - startAngle) / arcSegmentDivisions
    let direction = endAngle > startAngle ? 1 : -1
    let stopAngle = endAngle + (angleStep * direction)

    var prevPosX = null
    var prevPosY = null
    for(var angle=startAngle; direction > 0 ? (angle <= stopAngle) : (angle >= stopAngle); angle += angleStep * direction) {
      let degrees = direction > 0 ? Math.min(angle, endAngle) : Math.max(angle, endAngle)
      let cos = Math.cos(degrees * (Math.PI/180.0))
      let sin = Math.sin(degrees * (Math.PI/180.0))

      let arcX = cos * radius
      let arcY = sin * radius

      if(prevPosX !== null && prevPosY !== null) {
        vertices.push(prevPosX, prevPosY, 0)
        vertices.push(arcX, arcY, 0)
      }
      prevPosX = arcX
      prevPosY = arcY
    }

    return vertices
  }

  /**
   * Check for intersections between the object and a ray
   * Returns an object containing intersection point & object position
   * Returns null if no intersection occurred
   */
  checkRayIntersection(ray, worldRadius) {
    // If the scene object is invisible (eg, the dimension is auto-hidden) then don't do any intersections
    if(!this.sceneObject.visible)
      return null

    // Note: We only do intersections with the label
    // We currently don't intersect with the dimensioning lines themselves
    var textBox = new THREE.Box3()
    let expandVec = new THREE.Vector3(worldRadius, worldRadius, 1)
    var targetInt = new THREE.Vector3()
    var pos = new THREE.Vector3()

    for(var label of this.labelList) {
      // Build a box around the label & grow by the radius for intersections
      if(label.textRenderInfo) {
        textBox.copy(label.geometry.boundingBox)
        textBox.applyMatrix4(label.matrixWorld)
        textBox.expandByVector(expandVec)

        if(ray.intersectBox(textBox, targetInt) !== null) {
          textBox.getCenter(pos)

          return {
            intersection: targetInt,
            position: pos,
            objectId: label.id,
            distance: targetInt.distanceTo(pos),
            layer: GlassDesignerTypes.EditorUILayers.editableLabel
          }
        }
      }
    }

    return null
  }


  /**
   * Generate the linear dimension measurement label text using config settings & units info
   */
  calculateLinearLabelText(distance) {
    let userConfig = this.editor.getUserConfig()
    let units = 'mm'

    var text = ''
    if(userConfig.displayDimensionTrailingZeros)
      text = distance.toFixed(userConfig.linearDimensionPrecision)
    else
      text = MathsUtils.roundNumber(distance, userConfig.linearDimensionPrecision).toString()

    if(userConfig.displayLinearDimensionUnits)
      text += units

    return text
  }


  /**
   * Generate the angular dimension measurement label text using config settings & units info
   */
   calculateAngularLabelText(angle) {
    let userConfig = this.editor.getUserConfig()
    let units = '°'

    var text = ''
    if(userConfig.displayDimensionTrailingZeros)
      text = angle.toFixed(userConfig.angularDimensionPrecision)
    else
      text = MathsUtils.roundNumber(angle, userConfig.angularDimensionPrecision).toString()

    if(userConfig.displayAngularDimensionUnits)
      text += units

    return text
  }

}
